import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft, Ticket, Clock, User, ChatCircle, PaperclipHorizontal,
  CheckCircle, WarningCircle, CaretRight, Lock, Eye, Upload,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const priorityConfig = {
  critical: { label: 'Critical', cls: 'bg-red-100 text-red-700 border-red-200' },
  high: { label: 'High', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  medium: { label: 'Medium', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  low: { label: 'Low', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const statusConfig = {
  open: { label: 'Open', cls: 'bg-blue-100 text-blue-700' },
  assigned: { label: 'Assigned', cls: 'bg-indigo-100 text-indigo-700' },
  in_progress: { label: 'In Progress', cls: 'bg-violet-100 text-violet-700' },
  waiting_for_user: { label: 'Waiting for User', cls: 'bg-amber-100 text-amber-700' },
  waiting_for_payer: { label: 'Waiting for Payer', cls: 'bg-orange-100 text-orange-700' },
  escalated: { label: 'Escalated', cls: 'bg-red-100 text-red-700' },
  resolved: { label: 'Resolved', cls: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Closed', cls: 'bg-slate-100 text-slate-600' },
  reopened: { label: 'Reopened', cls: 'bg-rose-100 text-rose-700' },
};

const TicketDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [assignUsers, setAssignUsers] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resolutionText, setResolutionText] = useState('');

  const isAdmin = user?.roles?.includes('hospital_admin') || user?.role === 'hospital_admin';
  const isSuperAdmin = user?.role === 'super_admin' || user?.roles?.includes('super_admin');

  useEffect(() => { loadTicket(); loadUsers(); }, [id]);

  const loadTicket = async () => {
    setLoading(true);
    try {
      const res = await ticketsApi.get(id);
      setTicket(res.data);
      setResolutionText(res.data.resolution_summary || '');
    } catch (e) { toast.error('Failed to load ticket'); navigate('/tickets'); }
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    try { const res = await ticketsApi.assignableUsers(); setAssignUsers(res.data || []); } catch {}
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const payload = { status: newStatus };
      if (newStatus === 'resolved' && resolutionText) payload.resolution_summary = resolutionText;
      await ticketsApi.update(id, payload);
      toast.success(`Status changed to ${statusConfig[newStatus]?.label || newStatus}`);
      loadTicket();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setUpdating(false); }
  };

  const handleAssign = async (userId) => {
    setUpdating(true);
    try {
      await ticketsApi.update(id, { assigned_to: userId || '' });
      toast.success('Assignment updated');
      loadTicket();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setUpdating(false); }
  };

  const handlePriorityChange = async (priority) => {
    setUpdating(true);
    try {
      await ticketsApi.update(id, { priority });
      toast.success('Priority updated');
      loadTicket();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setUpdating(false); }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    setCommenting(true);
    try {
      await ticketsApi.addComment(id, { content: commentText, is_internal: isInternal });
      toast.success('Comment added');
      setCommentText('');
      loadTicket();
    } catch (e) { toast.error('Failed to add comment'); }
    finally { setCommenting(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await ticketsApi.uploadAttachment(id, file);
      toast.success('Attachment uploaded');
      loadTicket();
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" /></div>;
  if (!ticket) return null;

  const t = ticket;

  return (
    <div className="space-y-5" data-testid="ticket-detail-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/tickets')} className="text-slate-500" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono font-bold text-indigo-600" data-testid="ticket-id-display">{t.ticket_id}</span>
            <Badge className={`${priorityConfig[t.priority]?.cls || ''} border text-[10px]`}>{priorityConfig[t.priority]?.label}</Badge>
            <Badge className={`${statusConfig[t.status]?.cls || ''} border-0 text-[10px]`}>{statusConfig[t.status]?.label}</Badge>
            {t.is_overdue && <Badge className="bg-red-100 text-red-700 border-red-200 border text-[10px] gap-1"><WarningCircle className="h-3 w-3" weight="fill" /> Overdue</Badge>}
          </div>
          <h1 className="text-lg font-bold font-['Outfit'] text-slate-800 mt-1" data-testid="ticket-subject">{t.subject}</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-5">
          {/* Description */}
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="ticket-description-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold font-['Outfit'] text-slate-700">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{t.description}</p>
            </CardContent>
          </Card>

          {/* Resolution (for resolved/closed) */}
          {(t.status === 'resolved' || t.status === 'closed') && t.resolution_summary && (
            <Card className="bg-emerald-50 border border-emerald-200">
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-emerald-800 mb-1">Resolution Summary</p>
                <p className="text-sm text-emerald-700">{t.resolution_summary}</p>
              </CardContent>
            </Card>
          )}

          {/* Comments */}
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="comments-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold font-['Outfit'] text-slate-700 flex items-center gap-2">
                <ChatCircle className="h-4 w-4 text-indigo-500" /> Comments & Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Activity + Comments merged timeline */}
              {[
                ...(t.activities || []).map(a => ({ ...a, type: 'activity' })),
                ...(t.comments || []).map(c => ({ ...c, type: 'comment' })),
              ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((item, i) => (
                <div key={i} className={`flex gap-3 ${item.type === 'activity' ? '' : ''}`}>
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                    item.type === 'comment' ? (item.is_internal ? 'bg-amber-100' : 'bg-indigo-100') : 'bg-slate-100'
                  }`}>
                    {item.type === 'comment' ? (
                      item.is_internal ? <Lock className="h-3.5 w-3.5 text-amber-600" /> : <ChatCircle className="h-3.5 w-3.5 text-indigo-600" />
                    ) : (
                      <CaretRight className="h-3.5 w-3.5 text-slate-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {item.type === 'comment' ? (
                      <div className={`rounded-lg p-3 ${item.is_internal ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50 border border-slate-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-slate-700">{item.user_name}</span>
                          {item.is_internal && <Badge className="bg-amber-100 text-amber-700 border-0 text-[9px]">Internal</Badge>}
                          <span className="text-[10px] text-slate-400">{formatDate(item.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{item.content}</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 py-1.5">
                        <span className="text-xs text-slate-500">{item.detail}</span>
                        <span className="text-[10px] text-slate-400">by {item.user_name} {formatDate(item.created_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Add Comment */}
              {t.status !== 'closed' && (
                <div className="pt-3 border-t border-slate-200 space-y-2">
                  <Textarea placeholder="Add a comment..." rows={3} value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    className="border-slate-200 resize-none text-sm" data-testid="comment-input" />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                      <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)}
                        className="rounded border-slate-300" data-testid="internal-checkbox" />
                      <Lock className="h-3 w-3" /> Internal note
                    </label>
                    <Button size="sm" disabled={commenting || !commentText.trim()} onClick={handleComment}
                      className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs gap-1" data-testid="post-comment-btn">
                      {commenting ? 'Posting...' : 'Post Comment'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Actions */}
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="ticket-actions-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold font-['Outfit'] text-slate-700">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Status transition */}
              {t.allowed_statuses?.length > 0 && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Change Status</Label>
                  {t.allowed_statuses.includes('resolved') && (
                    <div className="mb-2">
                      <Textarea placeholder="Resolution summary (recommended)..." rows={2}
                        value={resolutionText} onChange={e => setResolutionText(e.target.value)}
                        className="border-slate-200 resize-none text-xs mt-1" data-testid="resolution-input" />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {t.allowed_statuses.map(s => (
                      <Button key={s} variant="outline" size="sm" disabled={updating}
                        className={`text-[10px] h-7 ${statusConfig[s]?.cls || ''} border`}
                        onClick={() => handleStatusChange(s)} data-testid={`status-btn-${s}`}>
                        {statusConfig[s]?.label || s}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Assignment - Super Admin only */}
              {isSuperAdmin && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Assign To</Label>
                <Select value={t.assigned_to || 'none'} onValueChange={v => handleAssign(v === 'none' ? '' : v)}>
                  <SelectTrigger className="border-slate-200 mt-1 h-8 text-xs" data-testid="assign-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {assignUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              )}

              {/* Priority */}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Priority</Label>
                <Select value={t.priority} onValueChange={handlePriorityChange}>
                  <SelectTrigger className="border-slate-200 mt-1 h-8 text-xs" data-testid="priority-change-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Attachment */}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Attachment</Label>
                <label className="mt-1 flex items-center gap-2 text-xs text-indigo-600 cursor-pointer hover:underline">
                  <Upload className="h-3.5 w-3.5" />
                  {uploading ? 'Uploading...' : 'Upload File'}
                  <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="file-upload" />
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Details */}
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="ticket-details-sidebar">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold font-['Outfit'] text-slate-700">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5">
              {[
                { label: 'Category', value: t.category },
                { label: 'Sub-Category', value: t.sub_category || '-' },
                { label: 'Branch', value: t.branch_name || '-' },
                { label: 'Department', value: t.department || '-' },
                { label: 'Raised By', value: t.raised_by_name },
                { label: 'Assigned To', value: t.assigned_to_name || 'Unassigned' },
                { label: 'Created', value: formatDate(t.created_at) },
                { label: 'SLA Due', value: formatDate(t.sla_due), highlight: t.is_overdue },
                { label: 'Related Claim', value: t.related_claim_no || '-' },
                { label: 'Related Payer', value: t.related_payer_name || '-' },
              ].map((d, i) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{d.label}</span>
                  <span className={`text-xs font-medium ${d.highlight ? 'text-red-600' : 'text-slate-700'}`}>{d.value}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Attachments */}
          {t.attachments?.length > 0 && (
            <Card className="bg-white border border-slate-200 shadow-sm" data-testid="attachments-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold font-['Outfit'] text-slate-700 flex items-center gap-2">
                  <PaperclipHorizontal className="h-4 w-4 text-indigo-500" /> Attachments ({t.attachments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {t.attachments.map((a, i) => (
                  <a key={i} href={a.url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 hover:bg-indigo-50 transition-colors">
                    <PaperclipHorizontal className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                    <span className="text-xs text-indigo-600 truncate">{a.filename}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{(a.size / 1024).toFixed(0)} KB</span>
                  </a>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketDetailPage;
